import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cls from "./UserMemberPartners.module.scss";
import api from "@/utils/axios";
import { timeAgo } from "@/utils/timeAgo";

const UserMemberPartners = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [memberId, setMemberId] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadPartners = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError("");
      const res = await api.get(
        `/v1/admin/members/single/partners-info/${userId}`
      );
      setMemberId(res.data?.memberId || null);
      setPartners(res.data?.partners || []);
    } catch (err) {
      console.error("Error loading partners:", err);
      setError("Failed to load partners for this member.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleBack = () => {
    navigate(`/admin/members/single/${userId}`);
  };

  const handleAddPartner = () => {
    navigate(`/admin/members/single/partners-create/${userId}`);
  };

  const handleViewPartner = (partnerId) => {
    navigate(
      `/admin/members/single/partners-info/single/${userId}/${partnerId}`
    );
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  const formatLabel = (v) => {
    if (!v) return "";
    return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (loading) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back to Member
          </button>
          <div className={cls.titleBlock}>
            <h1 className={cls.title}>Member Partners</h1>
            <p className={cls.subtitle}>Loading partner information…</p>
          </div>
        </div>

        <div className={cls.loadingCard}>
          <span className={cls.spinner} />
          <span>Fetching partners from the Universe…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back to Member
          </button>
          <div className={cls.titleBlock}>
            <h1 className={cls.title}>Member Partners</h1>
            <p className={cls.subtitle}>Something went wrong.</p>
          </div>
        </div>

        <div className={cls.errorCard}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const activePartners = partners.filter((p) => !p.isDeleted);
  const deletedPartners = partners.filter((p) => p.isDeleted);

  return (
    <div className={cls.page}>
      {/* Header */}
      <div className={cls.headerRow}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back to Member
        </button>

        <div className={cls.titleBlock}>
          <h1 className={cls.title}>Member Partners</h1>
          <p className={cls.subtitle}>
            View all partners linked to this member account, with their real
            images, names, gender and relationship flags.
          </p>
        </div>

        <div className={cls.headerActions}>
          <button
            type="button"
            className={cls.addPartnerBtn}
            onClick={handleAddPartner}
          >
            + Add Partner
          </button>
        </div>
      </div>

      {/* Member meta strip */}
      <section className={cls.metaStrip}>
        <div className={cls.metaLeft}>
          <div className={cls.metaLabel}>Member Link</div>
          <div className={cls.metaValues}>
            <span className={cls.metaChip}>
              User ID:
              <span className={cls.metaChipValue}>{userId}</span>
            </span>
            {memberId && (
              <span className={cls.metaChip}>
                Member ID:
                <span className={cls.metaChipValue}>{memberId}</span>
              </span>
            )}
          </div>
        </div>
        <div className={cls.metaRight}>
          <div className={cls.metaStats}>
            <span className={cls.metaCount}>
              Active:{" "}
              <strong className={cls.metaCountHighlight}>
                {activePartners.length}
              </strong>
            </span>
            <span className={cls.metaCount}>
              Deleted:{" "}
              <strong className={cls.metaCountHighlight}>
                {deletedPartners.length}
              </strong>
            </span>
          </div>
        </div>
      </section>

      {/* Active partners */}
      <section className={cls.section}>
        <div className={cls.sectionHeader}>
          <h2 className={cls.sectionTitle}>Active Partners</h2>
          <p className={cls.sectionSubtitle}>
            Primary partners are highlighted with a stronger glow. Click on a
            card to open the full partner profile.
          </p>
        </div>

        {activePartners.length === 0 ? (
          <div className={cls.emptyState}>
            <p>No active partners found for this member.</p>
          </div>
        ) : (
          <div className={cls.cardGrid}>
            {activePartners.map((partner) => {
              const pp = partner.personal || {};
              const fullName = [pp.firstName, pp.lastName]
                .filter(Boolean)
                .join(" ")
                .trim();
              const genderLabel = pp.gender ? formatLabel(pp.gender) : "—";

              const relationshipTypeLabel = partner.relationshipType
                ? formatLabel(partner.relationshipType)
                : "Not set";
              const relationshipStatusLabel = partner.relationshipStatus
                ? formatLabel(partner.relationshipStatus)
                : "Not set";

              const imgUrl = pp.realImage?.url || null;

              return (
                <article
                  key={partner.id}
                  className={`${cls.partnerCard} ${
                    partner.isPrimary ? cls.partnerPrimary : ""
                  }`}
                  onClick={() => handleViewPartner(partner.id)}
                >
                  <div className={cls.partnerMedia}>
                    <div
                      className={
                        imgUrl
                          ? cls.partnerAvatarImage
                          : cls.partnerAvatarFallback
                      }
                      style={
                        imgUrl
                          ? { backgroundImage: `url(${imgUrl})` }
                          : undefined
                      }
                    >
                      {!imgUrl && (
                        <span className={cls.partnerInitials}>
                          {fullName ? fullName.charAt(0).toUpperCase() : "P"}
                        </span>
                      )}
                    </div>

                    {partner.isPrimary && (
                      <span className={cls.primaryBadge}>Primary</span>
                    )}
                  </div>

                  <div className={cls.partnerBody}>
                    <div className={cls.partnerMainRow}>
                      <h3 className={cls.partnerName}>
                        {fullName || "Unnamed Partner"}
                      </h3>
                      <span className={cls.partnerGender}>{genderLabel}</span>
                    </div>

                    <div className={cls.partnerMetaRow}>
                      <div className={cls.partnerMetaItem}>
                        <span className={cls.partnerMetaLabel}>
                          Relationship Type
                        </span>
                        <span className={cls.partnerMetaValue}>
                          {relationshipTypeLabel}
                        </span>
                      </div>
                      <div className={cls.partnerMetaItem}>
                        <span className={cls.partnerMetaLabel}>
                          Relationship Status
                        </span>
                        <span className={cls.partnerMetaValue}>
                          {relationshipStatusLabel}
                        </span>
                      </div>
                    </div>

                    <div className={cls.partnerFooter}>
                      <div className={cls.partnerDates}>
                        <span className={cls.partnerDateItem}>
                          Created:{" "}
                          <span className={cls.partnerDateHighlight}>
                            {formatDate(partner.createdAt)}
                          </span>
                          {partner.createdAt && (
                            <span className={cls.partnerMuted}>
                              {" "}
                              ({timeAgo(partner.createdAt)})
                            </span>
                          )}
                        </span>
                        <span className={cls.partnerDateItem}>
                          Updated:{" "}
                          <span className={cls.partnerDateHighlight}>
                            {formatDate(partner.updatedAt)}
                          </span>
                        </span>
                      </div>

                      <button
                        type="button"
                        className={cls.viewBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewPartner(partner.id);
                        }}
                      >
                        View Partner →
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* Deleted partners */}
      {deletedPartners.length > 0 && (
        <section className={cls.section}>
          <div className={cls.sectionHeader}>
            <h2 className={cls.sectionTitle}>Deleted Partners</h2>
            <p className={cls.sectionSubtitle}>
              These entries are marked as deleted but kept for audit and
              history.
            </p>
          </div>

          <div className={`${cls.cardGrid} ${cls.deletedGrid}`}>
            {deletedPartners.map((partner) => {
              const pp = partner.personal || {};
              const fullName = [pp.firstName, pp.lastName]
                .filter(Boolean)
                .join(" ")
                .trim();
              const genderLabel = pp.gender ? formatLabel(pp.gender) : "—";
              const relationshipTypeLabel = partner.relationshipType
                ? formatLabel(partner.relationshipType)
                : "Not set";
              const relationshipStatusLabel = partner.relationshipStatus
                ? formatLabel(partner.relationshipStatus)
                : "Not set";
              const imgUrl = pp.realImage?.url || null;

              return (
                <article
                  key={partner.id}
                  className={cls.partnerCardDeleted}
                  onClick={() => handleViewPartner(partner.id)}
                >
                  <div className={cls.partnerMedia}>
                    <div
                      className={
                        imgUrl
                          ? cls.partnerAvatarImage
                          : cls.partnerAvatarFallback
                      }
                      style={
                        imgUrl
                          ? { backgroundImage: `url(${imgUrl})` }
                          : undefined
                      }
                    >
                      {!imgUrl && (
                        <span className={cls.partnerInitials}>
                          {fullName ? fullName.charAt(0).toUpperCase() : "P"}
                        </span>
                      )}
                    </div>
                    <span className={cls.deletedBadge}>Deleted</span>
                  </div>

                  <div className={cls.partnerBody}>
                    <div className={cls.partnerMainRow}>
                      <h3 className={cls.partnerName}>
                        {fullName || "Unnamed Partner"}
                      </h3>
                      <span className={cls.partnerGender}>{genderLabel}</span>
                    </div>
                     <div className={cls.partnerMetaRow}>
                      <div className={cls.partnerMetaItem}>
                        <span className={cls.partnerMetaLabel}>
                          Relationship Type
                        </span>
                        <span className={cls.partnerMetaValue}>
                          {relationshipTypeLabel}
                        </span>
                      </div>
                      <div className={cls.partnerMetaItem}>
                        <span className={cls.partnerMetaLabel}>
                          Relationship Status
                        </span>
                        <span className={cls.partnerMetaValue}>
                          {relationshipStatusLabel}
                        </span>
                      </div>
                    </div>
                    <div className={cls.partnerFooter}>
                      <div className={cls.partnerDates}>
                        <span className={cls.partnerDateItem}>
                          Created:{" "}
                          <span className={cls.partnerDateHighlight}>
                            {formatDate(partner.createdAt)}
                          </span>
                        </span>
                        <span className={cls.partnerDateItem}>
                          Updated:{" "}
                          <span className={cls.partnerDateHighlight}>
                            {formatDate(partner.updatedAt)}
                          </span>
                        </span>
                      </div>
                          <button
                        type="button"
                        className={cls.viewBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewPartner(partner.id);
                        }}
                      >
                        View Partner →
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default UserMemberPartners;
